import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { shopSubscriptionService } from "../services/shopSubscriptionService";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import ManualShopSubscriptionModal from "./ManualShopSubscriptionModal";
import { PlusCircle } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Number(n ?? 0));
const fcfa = (n) => `${fmt(Math.round(Number(n ?? 0)))} F`;
const dateFr = (d) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "–";

const durationLabel = (t) =>
  ({ monthly: "Mensuel", semi_annual: "Semestriel", annual: "Annuel" }[t] || t || "–");

const statusPill = (s) => {
  const map = {
    active: { t: "Actif", c: "bg-green-50 text-green-700" },
    expired: { t: "Expiré", c: "bg-red-50 text-red-700" },
    pending: { t: "En attente", c: "bg-amber-50 text-amber-700" },
    cancelled: { t: "Annulé", c: "bg-gray-100 text-gray-600" },
  };
  const it = map[s] || { t: s, c: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${it.c}`}>
      {it.t}
    </span>
  );
};

const ShopSubscriptionList = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["shop-subscriptions", page, status, paymentStatus, search],
    queryFn: () =>
      shopSubscriptionService.getSubscriptions({
        page,
        status: status === "all" ? null : status,
        payment_status: paymentStatus === "all" ? null : paymentStatus,
        search: search || null,
      }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id }) =>
      shopSubscriptionService.updateSubscriptionStatus(id, {
        status: "active",
        payment_status: "paid",
      }),
    onSuccess: () => queryClient.invalidateQueries(["shop-subscriptions"]),
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="text-sm text-red-500 p-4">
        Erreur lors du chargement des abonnements boutiques.
      </div>
    );

  const subscriptions = data?.data || [];
  const meta = {
    total: data?.total || 0,
    per_page: data?.per_page || 20,
    current_page: data?.current_page || 1,
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center gap-4">
        <div>
          <h2 className="text-base font-medium text-gray-800">Abonnements boutiques</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {meta.total} abonnement(s) — paliers Essentiel / Pro / Élite.
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-primary text-white px-3 py-1.5 rounded-md text-sm hover:bg-primary/90 cursor-pointer"
        >
          <PlusCircle size={16} />
          Activation manuelle
        </button>
      </div>

      {/* Filtres compacts */}
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Rechercher (boutique, utilisateur)"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-64"
        />
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="expired">Expiré</option>
          <option value="pending">En attente</option>
        </select>
        <select
          value={paymentStatus}
          onChange={(e) => { setPaymentStatus(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
        >
          <option value="all">Tous les paiements</option>
          <option value="paid">Payé</option>
          <option value="pending">En attente</option>
          <option value="failed">Échoué</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Boutique</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Palier</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Durée</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Période</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Montant</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Statut</th>
              <th className="px-4 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length > 0 ? (
              subscriptions.map((sub) => (
                <tr key={sub.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-800">{sub.shop?.name || "–"}</div>
                    <div className="text-xs text-gray-500">
                      {sub.user?.firstname} {sub.user?.lastname} · {sub.user?.phone_number}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {sub.plan?.name ? (
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-600">
                        {sub.plan.name}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Sans palier</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{durationLabel(sub.plan_type)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {dateFr(sub.start_date)} → {dateFr(sub.end_date)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{fcfa(sub.amount)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {statusPill(sub.status)}
                      <span className={`text-[10px] font-medium ${sub.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>
                        {sub.payment_status === "paid" ? "Payé" : "Paiement en attente"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {sub.payment_status !== "paid" ? (
                      <button
                        onClick={() => updateStatusMutation.mutate({ id: sub.id })}
                        disabled={updateStatusMutation.isPending}
                        className="px-3 py-1.5 rounded-md text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 disabled:opacity-50 cursor-pointer"
                      >
                        Valider
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">Terminé</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">
                  Aucun abonnement trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={meta.total}
        itemsPerPage={meta.per_page}
        onPageChange={setPage}
      />

      <ManualShopSubscriptionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default ShopSubscriptionList;
